import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, useHistory } from 'react-router';
import { z } from 'zod';
import SpinnerCircular from '~/components/shared/Spinner/SpinnerCircular';
import { useAuth } from '~/contexts/AuthContext/AuthProvider';
import { useUser } from '~/contexts/UserContext/UserProvider';
import {
  ApplicationStatus,
  BankConnectionResult,
  CountryCode,
  FlowTypes,
} from '~/enums';
import {
  formatAmountWithoutCurrency,
  formatPersonalNumber,
} from '~/helpers/formatters.helper';
import { logToSentry } from '~/helpers/loggers.helper';
import {
  getLanguageBasedOnReferrerUrl,
  getLanguageFromLocalStorage,
} from '~/helpers/market.helper';
import { getMarketBasedOnHostname } from '~/helpers/utils';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import useQueryParams from '~/hooks/useQueryParams';
import { RootState } from '~/store';
import { pushToGtmLoanAction } from '~/store/actions/gtmActions';
import {
  getClientCompanies,
  getLoanApplication,
  patchApplication,
} from '~/store/actions/loanApplicationActions';
import {
  defaultLanguageToMarket,
  setLanguage,
  setMarket,
} from '~/store/slices/intl.slice';
import {
  removeClearedState,
  setBankConnectionError,
  setCompanyIsNew,
  updateApplicantData,
  updateCurrentStepNumber,
  updateFlowType,
  updateForm,
} from '~/store/slices/loanApplication.slice';
import { theme } from '~/styles/themes';
import { ConfirmationPage } from './components/ConfirmationPage/ConfirmationPage';
import LaunchView from './components/LaunchView/LaunchView';
import Steps from './components/Steps/Steps';
import { UserInfo } from '~/store/apis/endpoints/userManagementApi/userManagement.api';

const onboardingMarkets = [
  CountryCode.SE,
  CountryCode.FI,
  CountryCode.NL,
  CountryCode.NO,
  CountryCode.BE,
  CountryCode.DK,
  CountryCode.DE,
];

// NOTE: Use this to test a new market in onboarding in non-prod environments
// if (import.meta.env.MODE !== 'production') {
//   onboardingMarkets.push();
// }

const BANK_CONNECTION_RESULT_QUERY_STRING_KEY = 'bank_connection_result';

const LoanApplication = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [ready, setReady] = useState(false);
  const [pageIsRefreshing, setPageIsRefreshing] = useState(false);
  const { isAuthenticated } = useAuth();
  const { user, companies } = useUser();
  const queryParams = useQueryParams();
  const marketFromDomain = getMarketBasedOnHostname();
  const { loanApplication, intl } = useSelector((state: RootState) => state);
  const {
    flowType,
    applicationStatus,
    currentStepNumber,
    form,
  } = loanApplication;
  const { market } = intl;

  const applicationUuidParam = queryParams.get('applicationUuid');
  const flowTypeParam = queryParams.get('flow_type');
  const loanAmountParam = queryParams.get('loan_amount');
  const eskatParam = queryParams.get('eskat');
  const bankConnectionResultParam = queryParams.get(
    BANK_CONNECTION_RESULT_QUERY_STRING_KEY
  );

  useEffect(() => {
    const currentUrl = new URL(window.location.href);
    if (loanAmountParam) {
      dispatch(
        updateForm({
          amount: formatAmountWithoutCurrency(loanAmountParam),
        })
      );
    }

    if (flowTypeParam && loanAmountParam) {
      if (flowTypeParam === FlowTypes.Manual) {
        dispatch(updateFlowType(FlowTypes.Manual));
        dispatch(updateCurrentStepNumber(1));
      } else {
        window.history.replaceState({}, document.title, currentUrl);
      }
    }
  }, []);

  useEffect(() => {
    if (marketFromDomain) {
      dispatch(setMarket(marketFromDomain));
      const languageFromReferrerUrl = getLanguageBasedOnReferrerUrl();
      const languageFromLocalStorage = getLanguageFromLocalStorage();
      const language = languageFromLocalStorage ?? languageFromReferrerUrl;
      if (language) {
        dispatch(setLanguage(language));
      } else {
        dispatch(defaultLanguageToMarket(marketFromDomain));
      }
    } else {
      window.location.replace(
        window.location.href.replace('.com', `.${CountryCode.SE}`)
      );
      setPageIsRefreshing(true);
    }
  }, []);

  const syncApplicantWithLoggedInUser = (userInfo: UserInfo) => {
    dispatch(
      updateApplicantData({
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        phone: userInfo.phone,
        email: userInfo.email,
        personalNumber: userInfo?.personalNumber
          ? formatPersonalNumber(userInfo.personalNumber, market)
          : userInfo?.personalNumber,
        dateOfBirth: userInfo?.dateOfBirth,
      })
    );
  };

  useEffect(() => {
    if (user) {
      syncApplicantWithLoggedInUser(user);
    }
  }, [user]);

  const handleAuthenticationStatus = () => {
    dispatch(
      updateForm({
        applicantIdentified: isAuthenticated,
        applicantAuthenticated: isAuthenticated,
      })
    );

    if (isAuthenticated && user?.qcode) {
      dispatch(updateForm({ userCode: user.qcode }));
    }
  };

  useEffect(() => {
    const handleAuthenticatedState = async () => {
      handleAuthenticationStatus();

      // NOTE: this needs to be done before final patch
      // if its done after final patch, we will not have uuid when doing the final patch.
      // Any problem with doing this before handling url auth type?
      if (applicationUuidParam) {
        await dispatch(getLoanApplication(applicationUuidParam));
      }

      if (isAuthenticated) {
        // If users is authenticated from MyQred and visits onboarding.
        dispatch(updateFlowType(FlowTypes.Authenticated));
      }

      if (eskatParam && applicationUuidParam) {
        sessionStorage.removeItem('loan-application-uuid');
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.delete('eskat');
        history.replace({ search: currentUrl.search });
        if (eskatParam === 'connected') {
          dispatch(
            pushToGtmLoanAction({
              actionName: 'eskat_connected',
            })
          );
          dispatch(updateForm({ eskatConnected: true }));
          await dispatch(patchApplication({ currentStepCompleted: false }));
        } else {
          dispatch(
            pushToGtmLoanAction({
              actionName: 'eskat_connect_cancelled',
            })
          );
        }
      }

      if (bankConnectionResultParam && applicationUuidParam) {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.delete(BANK_CONNECTION_RESULT_QUERY_STRING_KEY);
        history.replace({ search: currentUrl.search });

        switch (bankConnectionResultParam) {
          case BankConnectionResult.Success:
            dispatch(updateForm({ bankConnected: true }));
            await dispatch(patchApplication({ currentStepCompleted: false }));
            break;
          case BankConnectionResult.Error:
          case BankConnectionResult.Cancelled:
            dispatch(setBankConnectionError(true));
            break;
          default:
            dispatch(setBankConnectionError(true));
            logToSentry(
              Error('Unknown bank_connection_result param'),
              `Unknown bank_connection_result param ${bankConnectionResultParam}`
            );
        }
        const isValidResultParam = z
          .nativeEnum(BankConnectionResult)
          .safeParse(bankConnectionResultParam).success;

        if (isValidResultParam) {
          dispatch(
            pushToGtmLoanAction({
              actionName: `bank_connection_${bankConnectionResultParam}`,
            })
          );
        }
      }

      setReady(true);
    };

    handleAuthenticatedState();
  }, []);

  /**
   * Add applicationUuid to url when its set in redux.
   */
  useEffect(() => {
    const { applicationUuid } = loanApplication.form;
    if (applicationUuid) {
      const url = new URL(window.location.href);
      url.searchParams.set('applicationUuid', applicationUuid);
      history.replace({ search: url.search });
    }
  }, [loanApplication.form.applicationUuid]);

  /**
   * Remove 'applicationUuid' from URL if action 'clearLoanApplicationState' has been called.
   */
  useEffect(() => {
    if (loanApplication.cleared) {
      const url = new URL(window.location.href);
      url.searchParams.delete('applicationUuid');
      history.replace({ search: url.search });
      dispatch(removeClearedState());
      document.cookie = `publicToken=; Max-Age=0`;
      if (isAuthenticated) {
        dispatch(updateFlowType(FlowTypes.Authenticated));
        if (user) {
          syncApplicantWithLoggedInUser(user);
        }
      }
      handleAuthenticationStatus();
    }
  }, [loanApplication.cleared]);

  const viewHandler = useMemo(() => {
    const shouldShowSteps =
      currentStepNumber === 1 ||
      (currentStepNumber > 1 && form.applicationUuid);
    switch (applicationStatus) {
      case ApplicationStatus.None:
        if (shouldShowSteps) {
          return <Steps />;
        }
        return <LaunchView />;
      case ApplicationStatus.ApplicationFinished:
      case ApplicationStatus.ApplicationUnderReview:
      case ApplicationStatus.OfferNotApproved:
      case ApplicationStatus.OfferApproved:
        return <ConfirmationPage />;

      default:
        logToSentry(
          Error('viewHandler switch ended up in default'),
          `viewHandler in Onboarding.tsx, applicationStatus is ${applicationStatus}`
        );
        return <LaunchView />;
    }
  }, [applicationStatus, flowType, currentStepNumber, form.applicationUuid]);

  useEffect(() => {
    if (isAuthenticated) {
      if (companies === undefined) return;
      if (companies.length) {
        dispatch(getClientCompanies());
      } else {
        dispatch(setCompanyIsNew(true));
      }
    }
  }, [isAuthenticated, companies]);

  const isMarketValid = useMemo(
    () =>
      onboardingMarkets.some(
        (availableMarket: CountryCode) => availableMarket === market
      ),
    [market]
  );

  useEffect(() => {
    if (!isMarketValid) {
      logToSentry(
        Error(`Unsupported market for onboarding`),
        `Unsupported onboarding market ${market}`
      );
    }
  }, [isMarketValid]);

  return isMarketValid ? (
    <>
      {ready && !pageIsRefreshing ? (
        <>{viewHandler}</>
      ) : (
        <SpinnerCircular color={theme.colors.secondaryGray} />
      )}
    </>
  ) : (
    <Redirect to="/404" />
  );
};

export default LoanApplication;
